import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Pagination,
} from "@mui/material";
import {
  Add,
  Close,
  DeleteOutline,
  EditOutlined,
  Refresh,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import axios from "../../api/axiosConfig";
import Loader from "../../components/Loader/Loader";

const initialPartnerForm = {
  country: "",
  name: "",
  show: 1,
  live: 1,
  icon: "",
  description: "",
};

const initialColorForm = {
  light: "#ffffff",
  dark: "#000000",
};

function General() {
  const [loading, setLoading] = useState(false);

  const [partners, setPartners] = useState([]);
  const [countries, setCountries] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);

  const [page] = useState(1);
  const [limit] = useState(100);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedPartner, setSelectedPartner] = useState(null);

  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [partnerFormOpen, setPartnerFormOpen] = useState(false);

  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerForm, setPartnerForm] = useState(initialPartnerForm);

  const [colorFormOpen, setColorFormOpen] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [colorForm, setColorForm] = useState(initialColorForm);

  const [assetTypeFormOpen, setAssetTypeFormOpen] = useState(false);
  const [editingPartnerAssetType, setEditingPartnerAssetType] = useState(null);
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState("");

  const [profileSearch, setProfileSearch] = useState("");
  const [profilePage, setProfilePage] = useState(0);
  const profilesPerPage = 10;

  const getErrorMessage = (error) =>
    error.response?.data?.message || error.message || "Something went wrong";

  const fetchPartners = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.post("/marketplace/fetch-mp-partners/", {
        page,
        limit,
      });

      setPartners(response.data?.data || []);
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  /*
   * Change these two endpoints if your existing country and asset-type
   * routes use different names.
   */
  const fetchSupportingData = useCallback(async () => {
    try {
      const [countryResponse, assetTypeResponse] = await Promise.all([
        axios.get("/marketplace/fetch-countries/"),
        axios.get("/marketplace/fetch-mp-asset-types/"),
      ]);

      setCountries(
        countryResponse.data?.data || countryResponse.data?.countries || [],
      );

      setAssetTypes(
        assetTypeResponse.data?.data ||
          assetTypeResponse.data?.assetTypes ||
          [],
      );
    } catch (error) {
      console.error("Error fetching supporting data:", error);
      toast.error(getErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    fetchPartners();
    fetchSupportingData();
  }, [fetchPartners, fetchSupportingData]);

  const fetchPartner = async (partnerId, openModal = true) => {
    try {
      setLoading(true);

      const response = await axios.get(
        `/marketplace/fetch-mp-partner/${partnerId}`,
      );

      const partnerData = response.data?.data || null;

      setSelectedPartner(partnerData);
      setProfilePage(0);
      setProfileSearch("");

      if (openModal) {
        setPartnerModalOpen(true);
      }

      return partnerData;
    } catch (error) {
      console.error("Error fetching partner:", error);
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedPartner = async () => {
    if (!selectedPartner?.id) return;

    await fetchPartner(selectedPartner.id, false);
  };

  const handleOpenCreatePartner = () => {
    setEditingPartner(null);
    setPartnerForm(initialPartnerForm);
    setPartnerFormOpen(true);
  };

  const handleOpenEditPartner = () => {
    if (!selectedPartner) return;

    setEditingPartner(selectedPartner);

    setPartnerForm({
      country: selectedPartner.country || "",
      name: selectedPartner.name || "",
      show: Number(selectedPartner.show) === 1 ? 1 : 0,
      live: Number(selectedPartner.live) === 1 ? 1 : 0,
      icon: selectedPartner.icon || "",
      description: selectedPartner.description || "",
    });

    setPartnerFormOpen(true);
  };

  const handlePartnerFormChange = (event) => {
    const { name, value } = event.target;

    setPartnerForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSavePartner = async () => {
    try {
      if (
        !partnerForm.country ||
        !partnerForm.name.trim() ||
        !partnerForm.icon.trim() ||
        !partnerForm.description.trim()
      ) {
        toast.error("Please complete all partner fields");
        return;
      }

      setLoading(true);

      const payload = {
        country: Number(partnerForm.country),
        name: partnerForm.name.trim(),
        show: Number(partnerForm.show),
        live: Number(partnerForm.live),
        icon: partnerForm.icon.trim(),
        description: partnerForm.description.trim(),
      };

      if (editingPartner?.id) {
        await axios.patch(
          `/marketplace/update-mp-partner/${editingPartner.id}`,
          payload,
        );

        toast.success("Partner updated successfully");

        await fetchPartners();
        await fetchPartner(editingPartner.id, false);
      } else {
        await axios.post("/marketplace/create-mp-partner/", payload);

        toast.success("Partner created successfully");
        await fetchPartners();
      }

      setPartnerFormOpen(false);
      setEditingPartner(null);
      setPartnerForm(initialPartnerForm);
    } catch (error) {
      console.error("Error saving partner:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = async () => {
    if (!selectedPartner?.id) return;

    const confirmed = window.confirm(`Delete ${selectedPartner.name}?`);

    if (!confirmed) return;

    try {
      setLoading(true);

      await axios.delete(
        `/marketplace/delete-mp-partner/${selectedPartner.id}`,
      );

      toast.success("Partner deleted successfully");

      setPartnerModalOpen(false);
      setSelectedPartner(null);

      await fetchPartners();
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateColor = () => {
    setEditingColor(null);
    setColorForm(initialColorForm);
    setColorFormOpen(true);
  };

  const handleOpenEditColor = (color) => {
    setEditingColor(color);

    setColorForm({
      light: color.light || "#ffffff",
      dark: color.dark || "#000000",
    });

    setColorFormOpen(true);
  };

  const handleSaveColor = async () => {
    if (!selectedPartner?.id) return;

    try {
      setLoading(true);

      const payload = {
        mp_partner: selectedPartner.id,
        light: colorForm.light,
        dark: colorForm.dark,
      };

      if (editingColor?.id) {
        await axios.patch(
          `/marketplace/update-mp-partner-color/${editingColor.id}`,
          payload,
        );

        toast.success("Partner colour updated successfully");
      } else {
        await axios.post("/marketplace/create-mp-partner-color/", payload);

        toast.success("Partner colour created successfully");
      }

      setColorFormOpen(false);
      setEditingColor(null);
      setColorForm(initialColorForm);

      await refreshSelectedPartner();
    } catch (error) {
      console.error("Error saving partner colour:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteColor = async (colorId) => {
    const confirmed = window.confirm("Delete this partner colour?");

    if (!confirmed) return;

    try {
      setLoading(true);

      await axios.delete(`/marketplace/delete-mp-partner-color/${colorId}`);

      toast.success("Partner colour deleted successfully");

      await refreshSelectedPartner();
    } catch (error) {
      console.error("Error deleting partner colour:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateAssetType = () => {
    setEditingPartnerAssetType(null);
    setSelectedAssetTypeId("");
    setAssetTypeFormOpen(true);
  };

  const handleOpenEditAssetType = (partnerAssetType) => {
    setEditingPartnerAssetType(partnerAssetType);
    setSelectedAssetTypeId(partnerAssetType.mp_asset_type || "");
    setAssetTypeFormOpen(true);
  };

  const handleSaveAssetType = async () => {
    if (!selectedPartner?.id) return;

    if (!selectedAssetTypeId) {
      toast.error("Please select an asset type");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        mp_partner: selectedPartner.id,
        mp_asset_type: Number(selectedAssetTypeId),
      };

      if (editingPartnerAssetType?.id) {
        await axios.patch(
          `/marketplace/update-mp-partner-asset-type/${editingPartnerAssetType.id}`,
          payload,
        );

        toast.success("Partner asset type updated successfully");
      } else {
        await axios.post("/marketplace/create-mp-partner-asset-type/", payload);

        toast.success("Partner asset type created successfully");
      }

      setAssetTypeFormOpen(false);
      setEditingPartnerAssetType(null);
      setSelectedAssetTypeId("");

      await refreshSelectedPartner();
    } catch (error) {
      console.error("Error saving partner asset type:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssetType = async (partnerAssetTypeId) => {
    const confirmed = window.confirm(
      "Remove this asset type from the partner?",
    );

    if (!confirmed) return;

    try {
      setLoading(true);

      await axios.delete(
        `/marketplace/delete-mp-partner-asset-type/${partnerAssetTypeId}`,
      );

      toast.success("Partner asset type deleted successfully");

      await refreshSelectedPartner();
    } catch (error) {
      console.error("Error deleting partner asset type:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getCountryName = (partner) => partner?.country_country?.name || "—";

  const getAssetTypeName = (partnerAssetType) =>
    partnerAssetType?.mp_asset_type_mp_asset_type?.name || "—";

  const availableAssetTypes = assetTypes.filter((assetType) => {
    if (editingPartnerAssetType?.mp_asset_type === assetType.id) {
      return true;
    }

    return !selectedPartner?.mp_partner_asset_types?.some(
      (item) => Number(item.mp_asset_type) === Number(assetType.id),
    );
  });

  const filteredProfiles =
    selectedPartner?.mp_partner_profiles?.filter((partnerProfile) => {
      const search = profileSearch.toLowerCase();

      return (
        partnerProfile.profile_profile?.name?.toLowerCase().includes(search) ||
        partnerProfile.profile_profile?.email?.toLowerCase().includes(search)
      );
    }) || [];

  const paginatedProfiles = filteredProfiles.slice(
    profilePage * profilesPerPage,
    profilePage * profilesPerPage + profilesPerPage,
  );

  return (
    <div>
      {loading && (
        <div className="overlay">
          <Loader />
        </div>
      )}

      <Box sx={{ mt: 2 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Marketplace Partners
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Manage partners, colours, asset types and linked profiles.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchPartners}
                sx={{
                  color: "black",
                  borderColor: "black",
                  "&:hover": {
                    borderColor: "black",
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                Refresh
              </Button>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleOpenCreatePartner}
                sx={{
                  backgroundColor: "black",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#222",
                  },
                }}
              >
                Add Partner
              </Button>
            </Stack>
          </Stack>

          {partners.length === 0 ? (
            <Alert severity="info">No marketplace partners found.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Partner</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Show</TableCell>
                    <TableCell>Live</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {partners.map((partner) => (
                    <TableRow
                      hover
                      key={partner.id}
                      onClick={() => fetchPartner(partner.id)}
                      sx={{
                        cursor: "pointer",
                      }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            component="img"
                            src={partner.icon}
                            alt={partner.name}
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 1,
                              objectFit: "contain",
                              border: "1px solid #e5e5e5",
                              p: 0.5,
                            }}
                          />

                          <Box>
                            <Typography fontWeight={700}>
                              {partner.name}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {partner.id}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>{getCountryName(partner)}</TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            Number(partner.show) === 1 ? "Shown" : "Hidden"
                          }
                          variant={
                            Number(partner.show) === 1 ? "filled" : "outlined"
                          }
                          sx={{
                            backgroundColor:
                              Number(partner.show) === 1
                                ? "black"
                                : "transparent",
                            color:
                              Number(partner.show) === 1 ? "white" : "black",
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            Number(partner.live) === 1 ? "Live" : "Inactive"
                          }
                          variant={
                            Number(partner.live) === 1 ? "filled" : "outlined"
                          }
                          sx={{
                            backgroundColor:
                              Number(partner.live) === 1
                                ? "black"
                                : "transparent",
                            color:
                              Number(partner.live) === 1 ? "white" : "black",
                          }}
                        />
                      </TableCell>

                      <TableCell>{partner.description || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2 }}
          >
            Page {page} of {totalPages}
          </Typography>
        </Paper>
      </Box>

      {/* Partner details modal */}
      <Dialog
        open={partnerModalOpen}
        onClose={() => setPartnerModalOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              {selectedPartner?.name || "Partner Details"}
            </Typography>

            <IconButton onClick={() => setPartnerModalOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {selectedPartner && (
            <Stack spacing={4}>
              <Box>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      component="img"
                      src={selectedPartner.icon}
                      alt={selectedPartner.name}
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 2,
                        objectFit: "contain",
                        border: "1px solid #e5e5e5",
                        p: 1,
                      }}
                    />

                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedPartner.name}
                      </Typography>

                      <Typography color="text.secondary">
                        {getCountryName(selectedPartner)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<EditOutlined />}
                      onClick={handleOpenEditPartner}
                      sx={{
                        color: "black",
                        borderColor: "black",
                      }}
                    >
                      Update
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteOutline />}
                      onClick={handleDeletePartner}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>

                    <Typography>
                      {selectedPartner.description || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Show
                    </Typography>

                    <Typography>
                      {Number(selectedPartner.show) === 1 ? "Yes" : "No"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Live
                    </Typography>

                    <Typography>
                      {Number(selectedPartner.live) === 1 ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Colours */}
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Partner Colours
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Light and dark mode colour combinations.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenCreateColor}
                    sx={{
                      backgroundColor: "black",
                      "&:hover": {
                        backgroundColor: "#222",
                      },
                    }}
                  >
                    Add Colour
                  </Button>
                </Stack>

                {selectedPartner.mp_partner_colors?.length ? (
                  <Grid container spacing={2}>
                    {selectedPartner.mp_partner_colors.map((color) => (
                      <Grid item xs={12} md={6} key={color.id}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Typography fontWeight={700}>
                                Colour #{color.id}
                              </Typography>

                              <Stack direction="row">
                                <Tooltip title="Update">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEditColor(color)}
                                  >
                                    <EditOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteColor(color.id)}
                                  >
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Box
                                  sx={{
                                    minHeight: 90,
                                    borderRadius: 2,
                                    border: "1px solid #ddd",
                                    backgroundColor: color.light,
                                    display: "flex",
                                    alignItems: "flex-end",
                                    p: 1,
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    label={`Light: ${color.light}`}
                                    sx={{
                                      backgroundColor: "white",
                                      color: "black",
                                    }}
                                  />
                                </Box>
                              </Grid>

                              <Grid item xs={6}>
                                <Box
                                  sx={{
                                    minHeight: 90,
                                    borderRadius: 2,
                                    border: "1px solid #ddd",
                                    backgroundColor: color.dark,
                                    display: "flex",
                                    alignItems: "flex-end",
                                    p: 1,
                                  }}
                                >
                                  <Chip
                                    size="small"
                                    label={`Dark: ${color.dark}`}
                                    sx={{
                                      backgroundColor: "white",
                                      color: "black",
                                    }}
                                  />
                                </Box>
                              </Grid>
                            </Grid>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Alert severity="info">
                    No colours have been added for this partner.
                  </Alert>
                )}
              </Box>

              <Divider />

              {/* Asset types */}
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Asset Types
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Asset categories supported by this partner.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenCreateAssetType}
                    sx={{
                      backgroundColor: "black",
                      "&:hover": {
                        backgroundColor: "#222",
                      },
                    }}
                  >
                    Add Asset Type
                  </Button>
                </Stack>

                {selectedPartner.mp_partner_asset_types?.length ? (
                  <Stack spacing={1}>
                    {selectedPartner.mp_partner_asset_types.map(
                      (partnerAssetType) => (
                        <Paper
                          variant="outlined"
                          key={partnerAssetType.id}
                          sx={{ p: 2 }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box>
                              <Typography fontWeight={700}>
                                {getAssetTypeName(partnerAssetType)}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {
                                  partnerAssetType?.mp_asset_type_mp_asset_type
                                    ?.description
                                }
                              </Typography>
                            </Box>

                            <Stack direction="row">
                              <IconButton
                                onClick={() =>
                                  handleOpenEditAssetType(partnerAssetType)
                                }
                              >
                                <EditOutlined />
                              </IconButton>

                              <IconButton
                                color="error"
                                onClick={() =>
                                  handleDeleteAssetType(partnerAssetType.id)
                                }
                              >
                                <DeleteOutline />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </Paper>
                      ),
                    )}
                  </Stack>
                ) : (
                  <Alert severity="info">
                    No asset types have been added for this partner.
                  </Alert>
                )}
              </Box>

              <Divider />

              {selectedPartner.mp_partner_profiles?.length ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search by name or email"
                      value={profileSearch}
                      onChange={(e) => {
                        setProfileSearch(e.target.value);
                        setProfilePage(0);
                      }}
                    />
                  </Box>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Partner ID</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedProfiles.map((partnerProfile) => (
                          <TableRow key={partnerProfile.id}>
                            <TableCell>
                              {partnerProfile.profile_profile?.name || "—"}
                            </TableCell>

                            <TableCell>
                              {partnerProfile.profile_profile?.email || "—"}
                            </TableCell>

                            <TableCell>
                              {partnerProfile.partner_id || "—"}
                            </TableCell>

                            <TableCell>
                              {partnerProfile.date
                                ? new Date(partnerProfile.date).toLocaleString()
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {filteredProfiles.length > profilesPerPage && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 2,
                      }}
                    >
                      <Pagination
                        page={profilePage + 1}
                        count={Math.ceil(
                          filteredProfiles.length / profilesPerPage,
                        )}
                        onChange={(e, value) => setProfilePage(value - 1)}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  No profiles are currently linked to this partner.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/update partner modal */}
      <Dialog
        open={partnerFormOpen}
        onClose={() => setPartnerFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingPartner ? "Update Partner" : "Create Partner"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Partner name"
              name="name"
              value={partnerForm.name}
              onChange={handlePartnerFormChange}
            />

            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>

              <Select
                label="Country"
                name="country"
                value={partnerForm.country}
                onChange={handlePartnerFormChange}
              >
                {countries.map((countryItem) => (
                  <MenuItem key={countryItem.id} value={countryItem.id}>
                    {countryItem.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Icon URL"
              name="icon"
              value={partnerForm.icon}
              onChange={handlePartnerFormChange}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              name="description"
              value={partnerForm.description}
              onChange={handlePartnerFormChange}
            />

            <Stack direction="row" spacing={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Number(partnerForm.show) === 1}
                    onChange={(event) =>
                      setPartnerForm((current) => ({
                        ...current,
                        show: event.target.checked ? 1 : 0,
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "black",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "black",
                        },
                    }}
                  />
                }
                label="Show"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={Number(partnerForm.live) === 1}
                    onChange={(event) =>
                      setPartnerForm((current) => ({
                        ...current,
                        live: event.target.checked ? 1 : 0,
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "black",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "black",
                        },
                    }}
                  />
                }
                label="Live"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setPartnerFormOpen(false)}
            sx={{ color: "black" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSavePartner}
            sx={{
              backgroundColor: "black",
              "&:hover": {
                backgroundColor: "#222",
              },
            }}
          >
            {editingPartner ? "Update Partner" : "Create Partner"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/update colour modal */}
      <Dialog
        open={colorFormOpen}
        onClose={() => setColorFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingColor ? "Update Colour" : "Create Colour"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Light Mode
              </Typography>

              <TextField
                fullWidth
                type="color"
                value={colorForm.light}
                onChange={(event) =>
                  setColorForm((current) => ({
                    ...current,
                    light: event.target.value,
                  }))
                }
                inputProps={{
                  style: {
                    height: 60,
                    padding: 5,
                    cursor: "pointer",
                  },
                }}
              />

              <TextField
                fullWidth
                value={colorForm.light}
                onChange={(event) =>
                  setColorForm((current) => ({
                    ...current,
                    light: event.target.value,
                  }))
                }
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>
                Dark Mode
              </Typography>

              <TextField
                fullWidth
                type="color"
                value={colorForm.dark}
                onChange={(event) =>
                  setColorForm((current) => ({
                    ...current,
                    dark: event.target.value,
                  }))
                }
                inputProps={{
                  style: {
                    height: 60,
                    padding: 5,
                    cursor: "pointer",
                  },
                }}
              />

              <TextField
                fullWidth
                value={colorForm.dark}
                onChange={(event) =>
                  setColorForm((current) => ({
                    ...current,
                    dark: event.target.value,
                  }))
                }
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setColorFormOpen(false)}
            sx={{ color: "black" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveColor}
            sx={{
              backgroundColor: "black",
              "&:hover": {
                backgroundColor: "#222",
              },
            }}
          >
            {editingColor ? "Update Colour" : "Create Colour"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/update asset type modal */}
      <Dialog
        open={assetTypeFormOpen}
        onClose={() => setAssetTypeFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingPartnerAssetType ? "Update Asset Type" : "Add Asset Type"}
        </DialogTitle>

        <DialogContent dividers>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Asset Type</InputLabel>

            <Select
              label="Asset Type"
              value={selectedAssetTypeId}
              onChange={(event) => setSelectedAssetTypeId(event.target.value)}
            >
              {availableAssetTypes.map((assetType) => (
                <MenuItem key={assetType.id} value={assetType.id}>
                  {assetType.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setAssetTypeFormOpen(false)}
            sx={{ color: "black" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveAssetType}
            sx={{
              backgroundColor: "black",
              "&:hover": {
                backgroundColor: "#222",
              },
            }}
          >
            {editingPartnerAssetType ? "Update Asset Type" : "Add Asset Type"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </div>
  );
}

export default General;
